import { useGlobalSearchParams } from "expo-router";
import { useState } from "react";
import { Button } from "~/components/Button";
import { GoalForm, type GoalKey } from "~/components/form/resources/GoalForm";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Goals } from "~/entities/profile/goals";
import { useDismiss } from "~/providers/DismissProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";

export default function GoalDetails() {
    const { handleDismiss } = useDismiss();
    const { id }: { id: GoalKey } = useGlobalSearchParams();
    const { profile, can_update, saveGoals } = useProfile();
    const goal = profile?.goals[id] ?? null;
    const [editing, setEditing] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { addToast } = useFeedback();

    const doDelete = () => {
        if (!can_update && deleting) {
            return;
        }

        setDeleting(true);

        const payload = Goals.from({
            ...profile?.goals,
            [id]: null,
        });

        saveGoals(payload)
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Goal deleted",
                    duration: 3000,
                });

                setTimeout(handleDismiss);
            })
            .finally(() => setDeleting(false));
    };

    if (!goal) {
        setTimeout(handleDismiss);
    }

    return (
        <Authorize ability="view" section="goals">
            <FullPageModal
                asRoute
                title="Goal details"
                headerLeft={() => (
                    <Can ability="update" section="goals">
                        <Button
                            variant="quiet"
                            title="Edit"
                            disabled={!can_update}
                            onPress={() => setEditing(true)}
                            size="sm"
                        />
                        {confirm && (
                            <ConfirmationModal
                                onConfirm={doDelete}
                                title="Are you sure you want to delete this goal?"
                                confirmText="Delete goal"
                                confirmVariant="destructive-quiet"
                                loading={deleting}
                                onRequestClose={() => setConfirm(false)}
                            />
                        )}
                        {editing && <GoalForm id={id} onRequestClose={() => setEditing(false)} />}
                    </Can>
                )}
                withDone
                onRequestClose={handleDismiss}
                footer={() => (
                    <Can ability="update" section="goals">
                        <Button
                            icon="trash"
                            title="Delete goal"
                            variant="destructive"
                            size="lg"
                            onPress={() => setConfirm(true)}
                        />
                    </Can>
                )}
            >
                <DataList>
                    <DataListItem title="Goal" data={goal} />
                </DataList>
            </FullPageModal>
        </Authorize>
    );
}
